/**
 * Atmospheric polish — skybox gradient, distant fog, dynamic sun, time-of-day
 * tinting. Phase 6 polish that makes the world feel less «tech demo» and
 * more «game».
 */

import {
  Color3,
  Color4,
  CubeTexture,
  DirectionalLight,
  Effect,
  HDRCubeTexture,
  HemisphericLight,
  type Mesh,
  MeshBuilder,
  PBRMaterial,
  Scene,
  ShaderMaterial,
  StandardMaterial,
  Texture,
  Vector3,
  VertexBuffer,
} from '@babylonjs/core';

// R232q5aw — bullet-proof sky shader. Earlier StandardMaterial
// (disableLighting + emissive=(1,1,1)) interacted with the post-FX
// toneMapping + glow pipeline so the top of the dome rendered black
// while the horizon blew out to white. This dedicated 6-line shader
// just samples the per-vertex colour and writes it as the final
// fragment colour — no lighting, no fog, no env reflection, no glow.
const SKY_VERTEX = `
precision highp float;
attribute vec3 position;
attribute vec4 color;
uniform mat4 worldViewProjection;
varying vec4 vColor;
void main() {
  vColor = color;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}
`;
// R232q86z9: vertex colors are stored as linear (0..1) but render target
// expects sRGB encoded values. WebGL display path applies linear→sRGB only
// if the canvas has the sRGB pixel format — Babylon's default canvas does
// NOT, so writing 0.24 raw renders as 0.24 brightness on screen instead of
// the perceived (0.24)^(1/2.2)≈0.51. Encode to sRGB ourselves so the sky
// reads as the bright blue authored in paintGradient.
const SKY_FRAGMENT = `
precision highp float;
varying vec4 vColor;
vec3 linearToSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0/2.4)) - 0.055, step(vec3(0.0031308), c));
}
void main() {
  gl_FragColor = vec4(linearToSrgb(vColor.rgb), 1.0);
}
`;
let SKY_SHADER_REGISTERED = false;
function ensureSkyShaderRegistered(): void {
  if (SKY_SHADER_REGISTERED) return;
  Effect.ShadersStore['aiwariSkyVertexShader'] = SKY_VERTEX;
  Effect.ShadersStore['aiwariSkyFragmentShader'] = SKY_FRAGMENT;
  SKY_SHADER_REGISTERED = true;
}

// R232q86zci: textured sky shader. Samples panoramic PNG via uv on the
// inside of the sky-dome, emits full-bright (no lighting, no fog so the
// sky doesn't fade into the horizon haze). UV.x is yaw (0..1), UV.y is
// pitch (0=top, 1=bottom).
const SKY_TEX_VERTEX = `
precision highp float;
attribute vec3 position;
attribute vec2 uv;
uniform mat4 worldViewProjection;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}
`;
const SKY_TEX_FRAGMENT = `
precision highp float;
varying vec2 vUv;
uniform sampler2D skyTex;
void main() {
  // Babylon's sphere builder uses uv.y in [0..1] from south pole to
  // north pole. For a sky panorama the image goes top-to-bottom of
  // visible sky (sun upper, horizon lower), so flip v so that the top
  // of the image renders at the zenith.
  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
  gl_FragColor = vec4(texture2D(skyTex, uv).rgb, 1.0);
}
`;
let SKY_TEX_SHADER_REGISTERED = false;
function ensureSkyTexShaderRegistered(): void {
  if (SKY_TEX_SHADER_REGISTERED) return;
  Effect.ShadersStore['aiwariSkyTexVertexShader'] = SKY_TEX_VERTEX;
  Effect.ShadersStore['aiwariSkyTexFragmentShader'] = SKY_TEX_FRAGMENT;
  SKY_TEX_SHADER_REGISTERED = true;
}

export interface AtmosphereRig {
  setTimeOfDay: (hour: number) => void;
  dispose: () => void;
}

/**
 * Install a stylised sky dome + fog matched to the dome colour. Returns a rig
 * that allows callers to dial time-of-day in 0..24.
 */
export function installAtmosphere(scene: Scene): AtmosphereRig {
  // Big sky dome inside which all gameplay happens. We don't use Babylon's
  // SkyMaterial (heavy) — a vertex-coloured gradient sphere is cheap and
  // reads well, and we layer a Polyhaven HDRI on top as environmentTexture
  // for ambient PBR colour bleed when materials care.
  // R232q5ai: sky-dome — render-group 0 with depth-write disabled so the
  // dome NEVER occludes ground/scenery and is never occluded by far meshes.
  // Previous setup combined `disableLighting=true` with `emissiveColor=(1,1,1)`
  // which made the per-vertex gradient invisible (StandardMaterial in
  // unlit mode ignores vertex colours and renders flat emissive). User
  // reported a black void where the sky should be — the dome was rendering
  // but the gradient never reached the framebuffer. We now drive the sky
  // colour through `diffuseColor` + vertex colours with a small ambient
  // emissive so the dome reads in any lighting environment.
  // R232q5av: segments 24 → 48 + sideOrientation DOUBLE. The 24-segment
  // sphere had a noticeable polar pinch (huge triangles meeting at the
  // top vertex) — at certain camera tilts the pinch caused a small
  // black gap above the player's head. DOUBLE sideOrientation is a
  // belt-and-braces fix in case `backFaceCulling=false` is bypassed by
  // a late material override; with DOUBLE both face sets render no
  // matter what culling says.
  const dome = MeshBuilder.CreateSphere('sky-dome', { diameter: 8000, segments: 48, sideOrientation: 2 }, scene);
  // R232q5t: skybox MUST use infiniteDistance + apply-fog disabled, otherwise
  // when player walks even 50m from origin the skybox appears tiny on one
  // side and sky-coloured fog hits dome geometry → black horizon. Player
  // reported black void above ground; runtime patch confirmed this fix.
  dome.infiniteDistance = true;
  dome.applyFog = false;
  dome.isPickable = false;
  // Render BEFORE everything else so opaque ground always paints over it
  // — and so the dome never z-fights with distant landmarks.
  dome.renderingGroupId = 0;
  // R232q5av: dome has no infinite-distance reach into shadow / picking
  // pipelines — make absolutely sure it can never be culled by frustum
  // checks at camera roll extremes.
  dome.alwaysSelectAsActiveMesh = true;
  dome.doNotSyncBoundingInfo = true;
  // R232q5e — useVertexColors MUST be true BEFORE material assignment so
  // Babylon's shader cache emits the COLOR_0 attribute sampler.  Setting it
  // later (in paintGradient) was too late: shader had already cached the
  // unlit-emissive path without vertex colours, so the dome rendered as
  // flat white that subsequently got fogged to the horizon colour, leaving
  // the top of the screen black at most camera tilts (user 2026-05-04).
  dome.useVertexColors = true;
  dome.hasVertexAlpha = false;

  // R232q86zci: replace handmade dome material with explicit Babylon
  // approach: dome remains, but material reads texture via shader that
  // forces full-bright output (no lighting, no fog). Plus track texture
  // ref + base URL so time-of-day can swap to dusk/night PNGs.
  ensureSkyTexShaderRegistered();
  const skyMat = new ShaderMaterial(
    'sky-mat',
    scene,
    { vertex: 'aiwariSkyTex', fragment: 'aiwariSkyTex' },
    {
      attributes: ['position', 'uv'],
      uniforms: ['worldViewProjection'],
      samplers: ['skyTex'],
    },
  );
  skyMat.backFaceCulling = false;
  // 21.07: depth-write ON. With it off the dome's visibility depended on
  // undefined draw order inside rendering group 0 — some sessions painted
  // the panorama OVER the terrain (the intermittent «облачная пелена»).
  // With depth write the ordering is deterministic in both directions:
  // dome first → terrain passes LESS against 4000m; dome last → its
  // fragments fail LESS behind terrain and only fill the true sky.
  skyMat.disableDepthWrite = false;
  skyMat.fogEnabled = false;
  const SKY_BASE = `${import.meta.env.BASE_URL ?? '/'}sky`;
  const loadSkyTex = (id: 'day' | 'dusk' | 'night'): Texture => {
    const t = new Texture(`${SKY_BASE}/aiwari-${id}-sky.png`, scene, true, false);
    t.wrapU = Texture.WRAP_ADDRESSMODE;
    t.wrapV = Texture.CLAMP_ADDRESSMODE;
    t.hasAlpha = false;
    t.gammaSpace = false;
    return t;
  };
  let skyTex: Texture | null = loadSkyTex('day');
  let skyTexId: 'day' | 'dusk' | 'night' = 'day';
  skyMat.setTexture('skyTex', skyTex);
  dome.material = skyMat;
  // 21.07 FINAL sky-over-terrain fix: draw order inside one rendering group
  // is not guaranteed, and some sessions painted the panorama over the
  // world. Make the order structural instead: sky meshes stay in group 0,
  // EVERYTHING else moves to group 1 (groups render strictly 0 → 1, and we
  // keep the depth between groups). No more race, ever.
  scene.setRenderingAutoClearDepthStencil(1, false, false, false);
  const isSkyMesh = (m: { name?: string }) =>
    m === (dome as unknown) || m.name === 'engine-sky' || /^cloud-\d$/.test(m.name ?? '');
  const toWorldGroup = (m: { name?: string; renderingGroupId?: number }) => {
    if (isSkyMesh(m)) return;
    if ((m.renderingGroupId ?? 0) === 0) m.renderingGroupId = 1;
  };
  for (const m of scene.meshes) toWorldGroup(m);
  scene.onNewMeshAddedObservable.add((m) => toWorldGroup(m));
  // NOTE(21.07): do NOT dispose the legacy 'engine-sky' dome. Removing it
  // exposed the shader sky-dome painting flat blue OVER the terrain (its
  // panorama texture path/depth ordering needs a proper fix first). The
  // white-veil issue at some day-cycle hours is the lesser evil until then.
  // Keep paintGradient as fallback geometry visibility (vertex colors
  // unused by sky shader, but if shader compile fails Babylon falls back
  // to default visualization).
  paintGradient(dome, '#5DBEFF', '#88B8E8');
  // Keep old shader register no-op for compat.
  ensureSkyShaderRegistered();
  void Texture;
  void CubeTexture;
  void PBRMaterial;
  // CC0 Polyhaven HDR sky for ambient lighting + reflections on PBR
  // materials. Falls back gracefully if the file 404s.
  try {
    const hdr = new HDRCubeTexture(`${import.meta.env.BASE_URL}sky/kloppenheim-puresky.hdr`, scene, 256, false, true, false, true);
    scene.environmentTexture = hdr;
    // R232q86r: env intensity 0.6→0.0 — kloppenheim HDR имеет warm sunset
    // tint, который on PBR materials and ambient term красил skin в bronze.
    // Sky-dome остаётся самостоятельный (vertex-color gradient).
    scene.environmentIntensity = 0.0;
    // R232q5h2 hotfix: HDR is used ONLY for PBR ambient bleed — the visible
    // sky is the vertex-coloured 6000m dome above. createDefaultSkybox(1500m)
    // was smaller than the 7000m world, so once the player walked past
    // 1500m from origin they ended up OUTSIDE the skybox and saw nothing
    // (back-face culled), causing the black/blue empty-screen bug.
  } catch (e) {
    console.warn('[atmosphere] HDR load failed', (e as Error).message);
  }

  // R232q86zbs: fog density дальше уменьшен 0.00012 → 0.00006 + горизонт
  // теперь насыщенный sky-blue вместо почти-белого. User report 2026-05-09:
  // «небо не видно». Старая сетка 0.00012 + horizon-fade-to-white
  // съедала всё дальше 1500м в белый туман. 0.00006 → visibility 96% на 1km,
  // 87% на 2km, 60% на 4km. fogColor #61a8e0 заметно синее.
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogDensity = 0.00006;
  scene.fogColor = new Color3(0.38, 0.66, 0.88);
  scene.clearColor = new Color4(0.36, 0.62, 0.92, 1);

  // Improve the existing sun — find it (Engine.tsx created it as 'sun').
  const sun = scene.getLightByName('sun') as DirectionalLight | null;
  const hemi = scene.getLightByName('hemi') as HemisphericLight | null;
  // R232q5m: rim light (Engine.tsx default 0.55) contributes ~0.27 to ground
  // illumination — together with sun+hemi this pushes total over 2× and
  // Standard-material terrain clips to pure white. Reduce rim to 0.15.
  const rim = scene.getLightByName('rim') as DirectionalLight | null;
  if (rim) rim.intensity = 0.08;

  let currentHour = 12; // Полдень — максимум света

  function applyTimeOfDay(hour: number) {
    currentHour = hour;
    // Map hour → sun angle (0..24). Sun rises at 6, sets at 18.
    const t = ((hour - 6) / 12) * Math.PI; // 0 at sunrise, π at sunset
    const elevation = Math.sin(t);
    const direction = new Vector3(Math.cos(t) * 0.6, -elevation, -0.4);
    if (sun) {
      sun.direction = direction;
      // R232q86zas: Scene lights уменьшены — раньше sun 1.10 + hemi 0.65 +
      // rim 0.15 = 1.9 multiplier → character glow при diffuse 0.5+. Lower
      // intensities дают realistic skin tone.
      sun.diffuse = new Color3(1.0, 1.0, 1.0);
      sun.intensity = 0.6;
    }
    if (hemi) {
      hemi.intensity = 0.35;
      hemi.groundColor = new Color3(0.50, 0.50, 0.52);
      hemi.diffuse = new Color3(1.0, 1.0, 1.0);
    }
    scene.environmentIntensity = 0.0;
    // R128 / R232q86zbs: bright daytime sky — saturated blue at noon,
    // soft gold at dawn/dusk. Horizon at noon теперь #6FB5E8 (deep sky blue)
    // вместо почти-белого #B0E0F8 — иначе горизонт сливается с туманом
    // в белизну.
    const topColour = elevation > 0.2 ? '#1E76C8' : '#0F2854';
    const horizonColour =
      elevation > 0.5 ? '#6FB5E8' :
      elevation > 0.1 ? '#F4D88A' :
      elevation > -0.1 ? '#F4A688' :
      '#0E1428';
    paintGradient(dome, topColour, horizonColour);
    scene.fogColor = Color3.FromHexString(horizonColour).scale(0.95);

    // R232q86zci: swap sky panorama based on sun elevation.
    //  elevation > 0.30  → day (bright blue)
    //  elevation 0.0..0.30 → dusk (warm orange)
    //  elevation < 0.0   → night (stars + moon)
    const newId: 'day' | 'dusk' | 'night' =
      elevation > 0.30 ? 'day' :
      elevation > 0.0  ? 'dusk' :
      'night';
    if (newId !== skyTexId) {
      try { skyTex?.dispose(); } catch { /* nop */ }
      skyTex = loadSkyTex(newId);
      skyMat.setTexture('skyTex', skyTex);
      skyTexId = newId;
    }
  }

  applyTimeOfDay(currentHour);

  // R232q54: high-altitude cloud layer — 4 large translucent quads at y=2200
  // drifting eastward. Cheaper than ParticleSystem because each quad is one
  // draw call with a soft alpha-blended StandardMaterial. We use a procedural
  // gradient texture (canvas) when no cloud sprite exists, so the layer
  // works without shipping additional art.
  const cloudQuads: Mesh[] = [];
  // 21.07: procedural cloud quads DISABLED — four 4500m half-transparent
  // white planes drifting around the player merge into a full-screen veil
  // that occludes the terrain (the May 'noclouds' issue reborn). The
  // panoramic sky already has painted clouds.
  const CLOUD_QUADS_ENABLED = false;
  try {
    if (!CLOUD_QUADS_ENABLED) throw new Error('cloud quads disabled');
    const cloudMat = new StandardMaterial('cloud-mat', scene);
    cloudMat.disableLighting = true;
    cloudMat.backFaceCulling = false;
    cloudMat.alpha = 0.6;
    cloudMat.useAlphaFromDiffuseTexture = true;
    cloudMat.emissiveColor = new Color3(1, 1, 1);
    // Procedural cloud-puff texture — radial alpha falloff drawn to a 256
    // canvas. No external file → zero risk of 404.  Fast, soft, looks like
    // backlit cumulus from a distance.
    const cloudTex = makeCloudTexture(scene, 256);
    cloudMat.diffuseTexture = cloudTex;
    cloudMat.opacityTexture = cloudTex;
    cloudMat.fogEnabled = false;
    for (let i = 0; i < 4; i++) {
      const q = MeshBuilder.CreatePlane(`cloud-${i}`, { size: 4500 }, scene);
      q.material = cloudMat;
      q.rotation.x = Math.PI / 2; // face down
      q.position.y = 2200 + i * 80;
      // Spread quads around the player so 4 patches cover the whole hemisphere.
      const ang = (i / 4) * Math.PI * 2;
      q.position.x = Math.cos(ang) * 1800;
      q.position.z = Math.sin(ang) * 1800;
      q.isPickable = false;
      q.applyFog = false;
      q.renderingGroupId = 0;
      q.alwaysSelectAsActiveMesh = true;
      cloudQuads.push(q);
    }
    // Slow drift — 1 m/s east, recycled with a 9000m wrap so they never
    // disappear off the world's edge.
    const driftObserver = scene.onBeforeRenderObservable.add(() => {
      const dt = scene.getEngine().getDeltaTime() / 1000;
      for (const q of cloudQuads) {
        q.position.x += dt * 1.2;
        if (q.position.x > 4500) q.position.x -= 9000;
      }
    });
    (dome as unknown as { __cloudDriftObs?: typeof driftObserver }).__cloudDriftObs = driftObserver;
  } catch (exc) {
    if (typeof console !== 'undefined') {
      console.warn('[Atmosphere] cloud layer skipped:', exc);
    }
  }

  return {
    setTimeOfDay: applyTimeOfDay,
    dispose: () => {
      const obs = (dome as unknown as { __cloudDriftObs?: ReturnType<Scene['onBeforeRenderObservable']['add']> }).__cloudDriftObs;
      if (obs) scene.onBeforeRenderObservable.remove(obs);
      for (const q of cloudQuads) {
        (q.material as ShaderMaterial | null)?.dispose?.();
        q.dispose();
      }
      (dome.material as ShaderMaterial)?.dispose();
      dome.dispose();
    },
  };
}

/**
 * Build a 256×256 procedural radial-alpha cloud puff in a Canvas and wrap
 * it in a Babylon Texture. Pure white, soft falloff, ~60 % centre opacity.
 */
function makeCloudTexture(scene: Scene, size: number): Texture {
  if (typeof document === 'undefined') return new Texture(null, scene);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new Texture(null, scene);
  // Soft puff via radial gradient.
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0.0, 'rgba(255,255,255,0.85)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.45)');
  grad.addColorStop(1.0, 'rgba(255,255,255,0.0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new Texture(canvas.toDataURL('image/png'), scene, true, false, Texture.TRILINEAR_SAMPLINGMODE);
  tex.hasAlpha = true;
  tex.name = 'aiwari-cloud-puff';
  return tex;
}

function paintGradient(mesh: Mesh, topHex: string, horizonHex: string) {
  const top = Color3.FromHexString(topHex);
  const horizon = Color3.FromHexString(horizonHex);
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  if (!positions) return;
  const colours: number[] = [];
  // Mesh space — y range -radius..+radius. Map (-1..0..1) → (horizon..mid..top).
  // R232q5av: radius matches dome diameter 8000.
  const radius = 4000;
  for (let i = 0; i < positions.length; i += 3) {
    const y = positions[i + 1] / radius;
    const t = Math.max(0, Math.min(1, y * 0.5 + 0.5));
    const r = horizon.r * (1 - t) + top.r * t;
    const g = horizon.g * (1 - t) + top.g * t;
    const b = horizon.b * (1 - t) + top.b * t;
    colours.push(r, g, b, 1);
  }
  mesh.setVerticesData(VertexBuffer.ColorKind, colours);
  mesh.useVertexColors = true;
  // R232q5ai: explicit alpha disable — vertex colours include w=1 but
  // setting hasVertexAlpha=true makes Babylon classify the mesh as
  // transparent and re-sort it every frame, which can cause it to render
  // BEHIND fog when fog pass runs first → black sky. Force opaque path.
  mesh.hasVertexAlpha = false;
}
